import React, { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SearchInput } from './SearchInput';
import { ScopeChips } from './ScopeChips';
import { SearchResults } from './SearchResults';
import { useGlobalSearch } from '../../../hooks/useGlobalSearch';
import { useRecentItems } from '../../../hooks/useRecentItems';

const BACKEND_ONLY_SCOPES = new Set(['issue', 'doc', 'person']);

export const GlobalSearch = forwardRef(function GlobalSearch(
  {
    effectiveModuleKeySet,
    isSystemAdmin,
    isTenantAdmin,
    isFavorite,
    placeholder = 'Sök moduler, ärenden…',
  },
  ref,
) {
  const inputRef = useRef(null);
  const wrapRef = useRef(null);
  const [rawQuery, setRawQuery] = useState('');
  const [scope, setScope] = useState(null);
  const [focused, setFocused] = useState(false);
  const navigate = useNavigate();

  React.useImperativeHandle(ref, () => ({
    focus: () => {
      inputRef.current?.focus();
      inputRef.current?.select();
    },
    clear: () => setRawQuery(''),
  }), []);

  const recent = useRecentItems({ enabled: true });

  const recentRoutes = useMemo(() => {
    const set = new Set();
    for (const it of recent.items) set.add(it.route);
    return set;
  }, [recent.items]);

  const results = useGlobalSearch({
    rawQuery,
    scope,
    effectiveModuleKeySet,
    isSystemAdmin,
    isTenantAdmin,
    isFavorite,
    recentRoutes,
  });

  const activeScope = results.activeScope;
  const scopeBlocksAll =
    !!activeScope &&
    activeScope !== 'module' &&
    BACKEND_ONLY_SCOPES.has(activeScope);

  useEffect(() => {
    function onKey(e) {
      const isCmdK = (e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K');
      if (isCmdK) {
        e.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
        return;
      }
      if (e.key === 'Escape' && document.activeElement === inputRef.current) {
        inputRef.current?.blur();
        setRawQuery('');
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (!focused) return undefined;
    function onClickOutside(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setFocused(false);
      }
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [focused]);

  const handleSelect = useCallback(() => {
    setRawQuery('');
    setFocused(false);
    inputRef.current?.blur();
  }, []);

  const handleSubmit = useCallback((e) => {
    if (e.key !== 'Enter') return;
    const firstGroup = results.groups?.[0];
    const firstItem = firstGroup?.items?.[0];
    if (firstItem) {
      navigate(firstItem.module.to);
      handleSelect();
    }
  }, [results, navigate, handleSelect]);

  const open = focused || !!String(rawQuery || '').trim();

  return (
    <div
      className={`kp-search-wrap${open ? ' kp-search-wrap--open' : ''}`}
      ref={wrapRef}
      onKeyDown={handleSubmit}
    >
      <SearchInput
        ref={inputRef}
        value={rawQuery}
        onChange={setRawQuery}
        onFocus={() => setFocused(true)}
        onClear={() => { setRawQuery(''); inputRef.current?.focus(); }}
        placeholder={placeholder}
      />
      {open ? (
        <>
          <ScopeChips active={scope} onChange={setScope} />
          <SearchResults
            results={results}
            query={results.query}
            recent={recent}
            onClearRecent={recent.clear}
            onSelect={handleSelect}
            scopeBlocksAll={scopeBlocksAll}
            effectiveModuleKeySet={effectiveModuleKeySet}
          />
        </>
      ) : null}
    </div>
  );
});
